/**
 * One message, all the way down: who it went to, and what happened on each
 * channel for each of them. This is the screen that answers "did the reminder
 * reach Charles?" without anyone having to open SendGrid.
 */
import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MESSAGE_CATEGORY_LABELS, type DeliveryRow, type MessageCategory } from '@gwc/shared';
import { commsApi } from '../api/comms';
import { toast } from '../lib/toast';
import { Button, Select } from '../components/ui';
import { StatTile } from '../components/Widgets';
import { MESSAGE_STATUS_LABEL, StatusBadge, rollupSummary } from '../components/DeliveryBadges';

type Filter = 'all' | 'problems' | 'unread' | 'email' | 'push';

export function CommsMessage() {
  const { id = '' } = useParams();
  const qc = useQueryClient();
  const [filter, setFilter] = useState<Filter>('all');
  const { data, isLoading } = useQuery({
    queryKey: ['comms', 'message', id],
    queryFn: () => commsApi.message(id),
    refetchInterval: (q) => (q.state.data?.status === 'sent' ? false : 5_000),
  });

  const retry = useMutation({
    mutationFn: () => commsApi.retry(id),
    onSuccess: (r) => {
      toast.success(r.retried ? `Retrying ${r.retried} deliver${r.retried === 1 ? 'y' : 'ies'}.` : 'Nothing to retry.');
      void qc.invalidateQueries({ queryKey: ['comms', 'message', id] });
      void qc.invalidateQueries({ queryKey: ['comms', 'history'] });
    },
  });

  const rows = useMemo(() => {
    if (!data) return [];
    return data.recipients.filter((r) => {
      const ds = r.deliveries;
      switch (filter) {
        case 'problems':
          return ds.some((d) => d.status === 'failed' || d.status === 'bounced');
        case 'unread':
          return !r.readAt && !ds.some((d) => d.openedAt);
        case 'email':
          return ds.some((d) => d.channel === 'email');
        case 'push':
          return ds.some((d) => d.channel === 'push');
        default:
          return true;
      }
    });
  }, [data, filter]);

  if (isLoading || !data) return <p className="text-cream-300/60">Loading…</p>;
  const r = rollupSummary(data);
  const opened = data.recipients.filter((x) => x.deliveries.some((d) => d.openedAt)).length;

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <Link to="/comms?tab=history" className="text-xs text-cream-300/60 hover:text-gold-200">
        ← All messages
      </Link>

      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="font-display text-2xl text-cream-50">{data.title}</h1>
          <p className="mt-1 text-sm text-cream-300/60">
            {data.origin === 'system' ? 'Automatic' : data.senderName ?? 'Leadership'} ·{' '}
            {MESSAGE_STATUS_LABEL[data.status] ?? data.status}
            {data.sentAt ? ` · ${new Date(data.sentAt).toLocaleString()}` : ''}
            {' · '}
            {MESSAGE_CATEGORY_LABELS[data.category as MessageCategory] ?? data.category}
            {data.eventId ? (
              <>
                {' · '}
                <Link to={`/tastings/${data.eventId}`} className="hover:text-gold-200">
                  View tasting
                </Link>
              </>
            ) : null}
          </p>
        </div>
        {r.failed > 0 ? (
          <Button variant="ghost" loading={retry.isPending} onClick={() => retry.mutate()}>
            Retry {r.failed} failed
          </Button>
        ) : null}
      </header>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatTile label="Recipients" value={data.recipientCount} sub="inbox copies" />
        <StatTile
          label="Email"
          value={r.emailSent}
          sub={
            data.channels.includes('email')
              ? `${r.emailDelivered} confirmed delivered · ${r.emailFailed} failed`
              : 'not sent by email'
          }
        />
        <StatTile
          label="Opened / read"
          value={Math.max(opened, data.readCount)}
          sub={`${opened} email opens · ${data.readCount} read in app`}
        />
        <StatTile
          label="Push"
          value={r.pushSent}
          accent="wine"
          sub={data.channels.includes('push') ? `${r.pushFailed} failed` : 'not sent by push'}
        />
      </div>

      <section className="card p-5">
        <h2 className="mb-2 text-sm uppercase tracking-[0.15em] text-gold-400">Message</h2>
        <div className="whitespace-pre-wrap text-sm leading-relaxed text-cream-200">{data.body}</div>
        {data.link ? (
          <p className="mt-3 text-xs text-cream-300/50">Button links to {data.link}</p>
        ) : null}
      </section>

      <section className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-sm uppercase tracking-[0.15em] text-gold-400">
            Recipients ({rows.length})
          </h2>
          <Select value={filter} onChange={(e) => setFilter(e.target.value as Filter)} className="sm:w-56">
            <option value="all">Everyone</option>
            <option value="problems">Failed or bounced</option>
            <option value="unread">Not yet opened or read</option>
            <option value="email">Sent by email</option>
            <option value="push">Sent by push</option>
          </Select>
        </div>

        <div className="card overflow-x-auto p-0">
          <table className="w-full text-sm">
            <thead className="text-left text-[11px] uppercase tracking-wide text-cream-300/50">
              <tr className="border-b border-ink-800">
                <th className="px-4 py-2">Member</th>
                <th className="px-4 py-2">In app</th>
                <th className="px-4 py-2">Email</th>
                <th className="px-4 py-2">Push</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => {
                const email = p.deliveries.filter((d) => d.channel === 'email');
                const push = p.deliveries.filter((d) => d.channel === 'push');
                return (
                  <tr key={p.personId} className="border-b border-ink-800/60 last:border-0">
                    <td className="px-4 py-2 align-top">
                      <Link to={`/members/${p.personId}`} className="text-cream-100 hover:text-gold-200">
                        {p.firstName} {p.lastName}
                      </Link>
                      {p.email ? <p className="text-xs text-cream-300/50">{p.email}</p> : null}
                    </td>
                    <td className="px-4 py-2 align-top text-xs text-cream-300/70">
                      {p.readAt ? `Read ${new Date(p.readAt).toLocaleString()}` : 'Unread'}
                    </td>
                    <td className="px-4 py-2 align-top">
                      {email.length ? email.map((d) => <DeliveryCell key={d.id} d={d} />) : (
                        <span className="text-xs text-cream-300/40">
                          {data.channels.includes('email') ? 'Skipped (opted out / no email)' : '—'}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2 align-top">
                      {push.length ? push.map((d) => <DeliveryCell key={d.id} d={d} />) : (
                        <span className="text-xs text-cream-300/40">
                          {data.channels.includes('push') ? 'No device / muted' : '—'}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-4 py-6 text-center text-sm text-cream-300/50">
                    Nobody matches this filter.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function DeliveryCell({ d }: { d: DeliveryRow }) {
  const when =
    d.openedAt
      ? `opened ${new Date(d.openedAt).toLocaleString()}`
      : d.deliveredAt
        ? `delivered ${new Date(d.deliveredAt).toLocaleString()}`
        : d.sentAt
          ? `sent ${new Date(d.sentAt).toLocaleString()}`
          : d.attempts > 0
            ? `${d.attempts} attempt${d.attempts === 1 ? '' : 's'}`
            : 'queued';
  return (
    <div className="mb-1 last:mb-0">
      <div className="flex items-center gap-2">
        <StatusBadge status={d.status} />
        {d.clickedAt ? <span className="text-[10px] uppercase tracking-wide text-success">clicked</span> : null}
      </div>
      <p className="mt-0.5 text-[11px] text-cream-300/50">
        {when}
        {d.lastError ? <span className="text-danger"> · {d.lastError}</span> : null}
      </p>
    </div>
  );
}
